import React from "react";
import { X, ExternalLink, Download } from "lucide-react";

interface DocumentViewerModalProps {
  url: string;
  title?: string;
  onClose: () => void;
}

export const DocumentViewerModal: React.FC<DocumentViewerModalProps> = ({ url, title, onClose }) => {
  // Try to determine if it's an image or PDF based on extension or just use a generic object viewer.
  // Google Cloud Storage URLs might not have clear extensions but we can use an iframe/object for most.
  const isImage = url.match(/\.(jpeg|jpg|gif|png)(\?.*)?$/i) != null;
  const isPdf = url.match(/\.pdf(\?.*)?$/i) != null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        <div 
          className="fixed inset-0 transition-opacity" 
          aria-hidden="true" 
          onClick={onClose}
        >
          <div className="absolute inset-0 bg-gray-900 opacity-75"></div>
        </div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl w-full h-[85vh] flex flex-col">
          {/* Header */}
          <div className="bg-white px-4 py-3 border-b border-gray-200 flex justify-between items-center sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 truncate pr-4">
              {title || "Visualisation du document"}
            </h3>
            <div className="flex items-center space-x-3">
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-gray-500 transition-colors"
                title="Ouvrir dans un nouvel onglet"
              >
                <ExternalLink className="h-5 w-5" />
              </a>
              <a
                href={url}
                download
                className="text-gray-400 hover:text-gray-500 transition-colors"
                title="Télécharger"
              >
                <Download className="h-5 w-5" />
              </a>
              <button
                type="button"
                className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                onClick={onClose}
              >
                <span className="sr-only">Fermer</span>
                <X className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 bg-gray-100 p-4 flex items-center justify-center overflow-auto relative">
            {isImage ? (
              <img 
                src={url} 
                alt={title || "Document"} 
                className="max-h-full max-w-full object-contain shadow-sm"
              />
            ) : isPdf ? (
              <iframe
                src={`${url}#view=FitH`}
                className="w-full h-full border-0 rounded shadow-sm bg-white"
                title={title || "Document PDF"}
              />
            ) : (
              // Generic viewer object if type is unknown (like some gcs signed URLs)
              <object
                data={url}
                type="application/pdf"
                className="w-full h-full border-0 rounded shadow-sm bg-white"
                aria-label="Document viewer"
              >
                <iframe
                    src={url}
                    className="w-full h-full border-0 rounded shadow-sm bg-white"
                    title={title || "Document PDF fallback"}
                >
                    <div className="flex flex-col items-center justify-center h-full text-center p-6 gap-4">
                        <p className="text-gray-600">
                          Aperçu non disponible pour ce type de fichier dans le navigateur.
                        </p>
                        <a 
                          href={url} 
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                        >
                          Télécharger ou ouvrir le fichier
                        </a>
                    </div>
                </iframe>
              </object>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
